import {
  ConversationsAgent,
  ProgressUpdateStep,
  ResponseFormat,
} from "@mirage/service-dbx-api/service/grpc/context_engine_apiv2/gen/conversations_pb";

export type ExperimentSetting = "CONTROL" | "V1" | "V2" | "V3";

export interface NewUserMessage {
  message: string;
  conversationId: string;
  model?: ModelId;
  agentPayload?: string;
  llmIgnore?: boolean;
  rephrased?: string;
  attachments?: DocumentInfo[];
  agentIds?: AgentId[];
  clientContext?: ConversationClientContext;
  skipSearch?: boolean;
  messageType?: "userMessage" | "assistantMessage";
  source?: string;
}

export type MessageStatus = "success" | "pending" | "streaming" | "error";

export interface IconResource {
  lightUrl: string;
  darkUrl: string;
  lightPngUrl: string;
  darkPngUrl: string;
}

/**
 * These types are very similar to the types returned by the apiv2
 * endpoint as well as the GRCP streaming endpoint. But they are not
 * exactly the same, so this serves as the contract for this service.
 * If there are API updates in the future, we can decide to update this
 * type or try and populate new fields from existing data. It was a pain
 * updating this service to the streaming endpoint when the types were tied
 * to the apiv2 types.
 */
export interface DocumentInfo {
  url: string;
  title: string;
  // can't use bigint here because our KV storage doesn't support it
  lastUpdatedMs: number;
  brandedType: string;
  body: string;
  ownerId: string;
  thirdPartyId: string;
  relevanceScore: number;
  connectorInfo?: {
    connectorId: string;
    connectorType: number;
    displayName: string;
    platform: number;
    connectorName: string;
    connectorIconUrl: string;
    icon?: IconResource;
  };
  uuid: string;
  index: string;
  fileTypeInfo?: {
    id: string;
    displayName: string;
    icon?: IconResource;
  };
  transient?: boolean;
}

export interface ConversationSystemMessage {
  case: "systemMessage";
  value: {
    text: string;
  };
}

export interface ConversationUserMessage {
  case: "userMessage";
  value: {
    text: string;
    rephrased: string;
    attachments: DocumentInfo[];
    agentPayload?: string;
    llmIgnore?: boolean;
  };
}

export interface ConversationMessageJsonContent {
  content: string;
  followUpSuggestions: string[];
  draftModifications: string;
}

export interface ConversationAssistantMessage {
  case: "assistantMessage";
  value: {
    text: string;
    cited: DocumentInfo[];
    context?: DocumentInfo[];
    isCached: boolean;
    agentId?: string;
    format?: ResponseFormat;
  };
}

export interface ConversationUndefinedMessage {
  case: undefined;
  value?: undefined;
}

/**
 * This is the same type as ConversationsChatMessage, which is what the API
 * returns, with the exception of 'timestamp' and 'lastUpdatedMs' which have to be
 * converted to a Date from BigInt because KVStore does not support the latter,
 * as well as the addition of a few fields that are not in the API response.
 */
export interface ConversationMessage {
  id: string;
  messageType:
    | ConversationSystemMessage
    | ConversationUserMessage
    | ConversationAssistantMessage
    | ConversationUndefinedMessage;

  // Converted from BigInt to Date
  timestamp?: Date;

  // Additional client fields
  status: MessageStatus;
  jsonContent?: ConversationMessageJsonContent;
}

export interface ConversationSuggestedQuery {
  text: string;
  docIds: string[];
}

export interface Conversation {
  messages: ConversationMessage[]; // sorted by most recent last
  suggestedQueries: ConversationSuggestedQuery[]; // updated after each server response
  markdownDraft?: string;
  progressUpdates?: ProgressUpdateStep[];
  serverConversationId: string | undefined; // set after first response from the server
  writeAgentPayload?: string; // write agent payload from UserConversation.draft.write_agent_payload
  clientContext?: ConversationClientContext;
  assistRequestId?: string;
}

export interface ConversationClientContext {
  timezone: string;
  clientScenario: string;
  clientScenarioId: string;
}

// Conversation type with a local conversation ID (returned by methods that may create a new conversation)
export interface ConversationWithId extends Conversation {
  id: string; // local conversation ID
}

/**
 * Server metadata for a conversation.
 */
export interface ConversationMetadata {
  serverConversationId: string;
  created: Date;
  updated: Date;
  title: string;
  initialAgent?: string;
  preview: string;
}

// TODO: move non-conversation types to separate files

export interface WorkflowAgent {
  id: string;
  dataId?: string;
  name: string;
  description: string;
  icon: string;
  theme: string;
  workflow: WorkflowValues;
  createdBy: {
    display_name: string;
    profile_photo_url: string | undefined;
    email: string;
  };
  createdAtMs: number;
  updatedAtMs: number;
  accessType: "private" | "company";
}

export interface WorkflowValues {
  [step: number]: WorkflowStepValuesType;
}

export type WorkflowStepValuesType = {
  [key: string]: string | boolean | string[] | WorkflowStepValuesType;
};

export type WorkflowAgentRouteState = {
  agentId: string;
  documentIds: string[];
  model: ModelId;
};

export type ModelId = string;

// Represents a model that is available for use in conversations
export interface ModelInfo {
  // an opaque identifier for the model, used for model lookup
  // this is often a combination of provider and model name: ex: "openai:gpt-4"
  id: ModelId;

  // Human-readable name of the model to display in UI. ex: gpt-4o (OpenAI), Pixtral (Groq)
  displayName: string;

  // Brief description of the model's capabilities or recommended use cases
  description: string;

  // Indicates whether the model is certified for use
  // Certified models have been evaluated for quality and accuracy
  // Non-certified models may be less reliable and should be considered experimental
  isCertified: boolean;
}

export interface AvailableModels {
  models: ModelInfo[];
}

// NOTE: AgentId are the client-side and are what is used in dash repo
// These __should__ map to ConversationsAgent but mismatch in types are due to
// proto buff defs from rserver not being updated into this repo
export type AgentId = "conversations" | "write" | "summarize" | "rewrite";

export const mapConversationsAgentToAgentId: {
  [key in ConversationsAgent]?: AgentId[];
} = {
  [ConversationsAgent.WRITE]: ["write"],
  [ConversationsAgent.CONVERSATION]: ["conversations"],
  [ConversationsAgent.SUMMARIZE]: ["summarize"],
};
